import { useEffect, useRef, useState } from "react";
import { Button, openModal } from "risa-oasis-ui_v2";
import { PharmaFormFieldsProvider } from "../../context/pharmaFormFieldsContext";
import { CmmFormConfigModel } from "../../data-model/cmmFormConfigModel";
import {
  FormDataModel,
  FormField,
  SectionField,
} from "../../data-model/pharmaPaFormModel";
import AddSectionWithDocImage from "../../svg/addSectionWithDocImage";
import {
  deleteFieldByKey,
  findFieldByKey,
  insertFieldByParentId,
  sortFormFields,
  updateFieldByParentAndIndex,
} from "../../utils/formConfigUtils";
import SideModal from "../modals/filterModal/filterModal";
import FormConfigViewerModal from "../modals/formConfigViewerModal/formConfigViewerModal";
import FormFieldSideViewModal from "../modals/formFieldsSideViewModal/formFieldSideViewModal";
import { SectionSideModal } from "../modals/sectionSideModalModal/sectionSideModal";
import { RenderEachSectionOfTheForm } from "./renderSingleCardForFormConfig";

interface RenderFormConfigCardsProps {
  formConfigData?: CmmFormConfigModel | null;
  shouldShowSideModalForSection?: boolean;
  shouldCloseSideModalForSection?: (boolean) => void;
  shouldShowFormViewerModal?: boolean;
  shouldCloseFormViewerModal?: (boolean) => void;
  emitFinalData?: (data: FormDataModel) => void;
}

const RenderFormConfigCards = (props: RenderFormConfigCardsProps) => {
  const data = props.formConfigData;
  const [showSideModal, setShowSideModal] = useState(false);
  const parentKeyRef = useRef<string | null>(null);
  const [jsonData, setJsonData] = useState<FormDataModel>({
    formTitle: "",
    fields: [],
  });
  const [editField, setEditField] = useState<FormField | null>(null);
  const [editFieldType, setEditFieldType] = useState<"add" | "edit" | null>(
    null,
  );
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [sectionFieldType, setSectionFieldType] = useState<"add" | "edit">(
    "add",
  );
  const [shouldShowSectionSideModal, setShouldShowSectionSideModal] =
    useState(false);
  const [shouldShowFormViewerModal, setShouldShowFormViewerModal] =
    useState(false);

  useEffect(() => {
    if (data && data.data) {
      const sortedJson = {
        ...data?.data,
        fields: sortFormFields(data?.data.fields),
      };
      setJsonData(sortedJson);
    }
  }, [data]);

  useEffect(() => {
    props?.shouldShowSideModalForSection && setShouldShowSectionSideModal(true);
  }, [props?.shouldShowSideModalForSection]);

  useEffect(() => {
    props?.shouldShowFormViewerModal && setShouldShowFormViewerModal(true);
  }, [props?.shouldShowFormViewerModal]);

  useEffect(() => {
    shouldShowFormViewerModal && openModal("form-config-viewer-modal");
  }, [shouldShowFormViewerModal]);

  useEffect(() => {
    if (jsonData) {
      props?.emitFinalData?.(jsonData);
    }
  }, [jsonData]);

  const handleAddForm = (data: FormField) => {
    const existingForm = { ...jsonData };
    const newFormData = insertFieldByParentId(
      existingForm,
      parentKeyRef.current ?? "",
      data,
    );
    const sortedData = {
      ...newFormData,
      fields: sortFormFields(newFormData.fields),
    };
    setJsonData(sortedData);
    parentKeyRef.current = null;
    setEditFieldType(null);
  };

  const handleDeleteField = (key: string) => {
    const existingForm = { ...jsonData };
    const deletedForm = deleteFieldByKey(existingForm, key);

    const sortedData = {
      ...deletedForm,
      fields: sortFormFields(deletedForm.fields),
    };
    setJsonData(sortedData);
  };

  const handleEditField = (data: FormField) => {
    const existingForm = { ...jsonData };
    const newFormData = updateFieldByParentAndIndex(
      existingForm,
      parentKeyRef.current ?? "",
      editIndex ?? 0,
      data,
    );
    const sortedData = {
      ...newFormData,
      fields: sortFormFields(newFormData.fields),
    };
    setJsonData(sortedData);
    setEditField(null);
    setEditIndex(null);
    setEditFieldType(null);
  };

  const handleSectionSave = (data: SectionField) => {
    if (sectionFieldType === "edit") {
      const existingForm = { ...jsonData };
      const newFormData = updateFieldByParentAndIndex(
        existingForm,
        "",
        editIndex ?? 0,
        data as SectionField,
      );
      const sortedData = {
        ...newFormData,
        fields: sortFormFields(newFormData.fields),
      };
      setJsonData(sortedData);
    } else {
      const existingForm = { ...jsonData };
      const newFormData = insertFieldByParentId(
        existingForm,
        "",
        data as SectionField,
      );
      const sortedData = {
        ...newFormData,
        fields: sortFormFields(newFormData.fields),
      };
      setJsonData(sortedData);
    }

    setShouldShowSectionSideModal(false);
    props?.shouldCloseSideModalForSection?.(true);
    setSectionFieldType("add");
  };

  return (
    <div className="flex h-full flex-col gap-3 overflow-auto">
      {jsonData.fields.length === 0 && (
        <RenderSectionForAddingFormSection
          onClick={() => {
            setShouldShowSectionSideModal(true);
            setSectionFieldType("add");
          }}
        />
      )}
      {jsonData.fields
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((section, index) => (
          <RenderEachSectionOfTheForm
            index={index}
            key={section.key}
            section={section}
            onAddField={(parentKey) => {
              setShowSideModal(true);
              parentKeyRef.current = parentKey;
              setEditFieldType("add");
            }}
            onDeleteField={(key) => {
              handleDeleteField(key);
            }}
            onEditField={(parentKey, field, renderIndex) => {
              setShowSideModal(true);
              parentKeyRef.current = parentKey ?? "";
              setEditField(field ?? null);
              setEditIndex(renderIndex ?? 0);
              setEditFieldType("edit");
            }}
            onEditSection={(section, index) => {
              setShouldShowSectionSideModal(true);
              setSectionFieldType("edit");
              setEditField(findFieldByKey(jsonData, section) as FormField);
              setEditIndex(index);
            }}
            onDeleteSection={(key) => {
              handleDeleteField(key);
            }}
          />
        ))}
      {showSideModal && (
        <SideModal
          isOpen={showSideModal}
          onClose={() => setShowSideModal(false)}
          title="Add Field"
          id="form-config-modal"
        >
          <FormFieldSideViewModal
            type={editFieldType === "add" ? "add" : "edit"}
            onSubmit={(data) => {
              const dataToWrite = Object.keys(data).reduce((acc, key) => {
                if (data[key] !== undefined && data[key] !== "") {
                  acc[key] = data[key];
                }
                return acc;
              }, {});

              if (editFieldType === "edit") {
                handleEditField(dataToWrite as FormField);
              } else {
                handleAddForm(dataToWrite as FormField);
              }
            }}
            onClose={() => {
              setShowSideModal(false);
            }}
            initialData={
              editFieldType === "edit"
                ? (findFieldByKey(jsonData, editField?.key ?? "") as FormField)
                : undefined
            }
          />
        </SideModal>
      )}
      {shouldShowSectionSideModal && (
        <SideModal
          isOpen={shouldShowSectionSideModal}
          onClose={() => {
            setShouldShowSectionSideModal(false);
            props?.shouldCloseSideModalForSection?.(true);
            setSectionFieldType("add");
          }}
          title="Add Section"
          id="form-config-modal"
        >
          <SectionSideModal
            type={sectionFieldType === "add" ? "add" : "edit"}
            onSaveClick={(data) => {
              handleSectionSave(data as SectionField);
            }}
            onClose={() => {
              setShouldShowSectionSideModal(false);
              props?.shouldCloseSideModalForSection?.(true);
              setSectionFieldType("add");
            }}
            initialData={
              sectionFieldType === "edit"
                ? (editField as SectionField)
                : undefined
            }
          />
        </SideModal>
      )}
      {shouldShowFormViewerModal && (
        <PharmaFormFieldsProvider>
          <FormConfigViewerModal
            isOpen={shouldShowFormViewerModal}
            onClose={() => {
              setShouldShowFormViewerModal(false);
              props?.shouldCloseFormViewerModal?.(true);
            }}
            formData={jsonData}
          />
        </PharmaFormFieldsProvider>
      )}
    </div>
  );
};

export default RenderFormConfigCards;

const RenderSectionForAddingFormSection = ({
  onClick,
}: {
  onClick: () => void;
}) => {
  return (
    <div className="flex h-full flex-col items-center gap-3">
      <AddSectionWithDocImage />
      <span className="text-center text-sm text-primaryGray-1">
        Start creating form for insurance portal by adding section <br /> and
        then add fields to configure it
      </span>
      <div>
        <Button
          buttonType="primary"
          size="medium"
          disabled={false}
          onClick={onClick}
        >
          Add Section
        </Button>
      </div>
    </div>
  );
};
